"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { UserButton, useAuth } from "@clerk/nextjs";
import { motion } from "framer-motion";
import {
  Inbox,
  FolderOpen,
  Plus,
  Settings,
  User,
  Folders,
  Compass,
  BarChart3,
  Music,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { prefetchTodayItems, prefetchCategories } from "@/hooks/use-items";
import {
  prefetchProfileData,
  prefetchSettingsData,
} from "@/hooks/use-dashboard";
import { prefetchCollections } from "@/hooks/use-collections";
import { useCallback } from "react";
import { useTimezoneSync } from "@/hooks/use-timezone-sync";
import { Button } from "@/components/ui/button";
import { useIsAdmin } from "@/hooks/use-admin";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { LoadingScreen } from "@/components/LoadingScreen";
import { ExtensionPromoBanner } from "@/components/dashboard/ExtensionPromoBanner";
export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const { isLoaded, isSignedIn } = useAuth(); // Get isSignedIn state
  const { isAdmin } = useIsAdmin(); // Check if user is admin

  // Auto-detect and sync user's timezone for accurate streak tracking
  useTimezoneSync();

  // Prefetch functions for instant navigation
  const prefetchToday = useCallback(() => {
    prefetchTodayItems();
  }, []);

  const prefetchLibrary = useCallback(() => {
    prefetchCategories();
  }, []);

  const prefetchProfile = useCallback(() => {
    prefetchProfileData();
  }, []);

  const prefetchSettings = useCallback(() => {
    prefetchSettingsData();
  }, []);

  const prefetchCollectionsData = useCallback(() => {
    prefetchCollections();
  }, []);

  const navItems = [
    {
      href: "/today",
      label: "Inbox",
      icon: Inbox,
      isActive: pathname === "/today",
      onPrefetch: prefetchToday,
      protected: true,
    },
    {
      href: "/items",
      label: "Library",
      icon: FolderOpen,
      isActive: pathname === "/items" || pathname.startsWith("/items?"),
      onPrefetch: prefetchLibrary,
      onboardingTarget: "nav-library",
      protected: true,
    },
    {
      href: "/collections",
      label: "Collections",
      icon: Folders,
      isActive: pathname.startsWith("/collections"),
      onPrefetch: prefetchCollectionsData,
      protected: true,
    },
    {
      href: "/music",
      label: "Music",
      icon: Music,
      isActive: pathname === "/music",
      protected: true,
    },
    {
      href: "/discover",
      label: "Discover",
      icon: Compass,
      isActive: pathname === "/discover" || pathname.startsWith("/c/"),
      protected: false,
    },
    {
      href: "/profile",
      label: "Profile",
      icon: User,
      isActive: pathname === "/profile",
      onPrefetch: prefetchProfile,
      protected: true,
    },
  ];

  // If Clerk authentication is not yet loaded, render the neural loading screen.
  // This provides a premium entry experience immediately after login.
  if (!isLoaded) {
    return (
      <LoadingScreen
        message="Neural Check"
        submessage="Verifying secure access protocols"
      />
    );
  }

  // Filter nav items based on auth state
  const visibleNavItems = navItems.filter(
    (item) => !item.protected || isSignedIn,
  );

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Header */}
      <header className="bg-black/60 backdrop-blur-3xl border-b border-white/[0.1] sticky top-0 z-50 shadow-2xl shadow-black/80">
        <div className="max-w-7xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between gap-4">
            {/* Logo */}
            <Link
              href={isSignedIn ? "/today" : "/"}
              className="flex items-center group transition-transform duration-300 hover:scale-[1.02]"
            >
              {/* Mobile: Icon only */}
              <Image
                src="/icon-192.png"
                alt="Tavlo"
                width={28}
                height={32}
                className="block md:hidden"
                style={{
                  filter: "brightness(0) invert(1)",
                }}
              />
              {/* Desktop: Full logo */}
              <Image
                src="/logo-light.png"
                alt="Tavlo"
                width={110}
                height={40}
                className="hidden md:block"
                style={{
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-1.5 px-1.5 py-1 bg-white/[0.03] border border-white/[0.1] rounded-2xl">
              {visibleNavItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onMouseEnter={item.onPrefetch}
                  onFocus={item.onPrefetch}
                  data-onboarding={item.onboardingTarget}
                  className={cn(
                    "relative flex items-center gap-2 px-4 py-2 text-[13px] font-bold uppercase tracking-[0.1em] rounded-xl transition-all duration-300",
                    item.isActive
                      ? "text-white bg-white/[0.05] shadow-inner"
                      : "text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.02]",
                  )}
                >
                  <item.icon
                    className={cn(
                      "w-4 h-4 transition-colors duration-300",
                      item.isActive ? "text-primary" : "text-zinc-600",
                    )}
                  />
                  <span className="hidden md:inline">{item.label}</span>
                  {item.isActive && (
                    <motion.span
                      layoutId="nav-dot"
                      className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full shadow-[0_0_8px_rgba(var(--primary),0.8)]"
                    />
                  )}
                </Link>
              ))}

              {isSignedIn && (
                <div className="hidden sm:block ml-1 pl-1 border-l border-white/[0.05]">
                  <Link
                    href="/add"
                    data-onboarding="nav-add"
                    className="flex items-center gap-2 px-4 py-2 text-[13px] font-black uppercase tracking-[0.1em] text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-all duration-300 shadow-[0_0_20px_-5px_rgba(var(--primary),0.4)] active:scale-95"
                  >
                    <Plus className="w-4 h-4 stroke-[3]" />
                    <span>Add</span>
                  </Link>
                </div>
              )}
            </nav>

            {/* User Actions */}
            <div className="flex items-center gap-3">
              {isSignedIn ? (
                <>
                  <div className="flex items-center gap-1.5">
                    <NotificationCenter />
                    {isAdmin && (
                      <Link
                        href="/system/internal-analytics-dashboard"
                        className={cn(
                          "flex items-center justify-center p-2 rounded-xl transition-all duration-300 border",
                          pathname === "/system/internal-analytics-dashboard"
                            ? "text-primary bg-primary/10 border-primary/20"
                            : "text-zinc-500 border-white/[0.05] hover:text-zinc-200 hover:bg-white/[0.05] hover:border-white/[0.12]",
                        )}
                        title="Admin Dashboard"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Link>
                    )}
                    <Link
                      href="/settings"
                      onMouseEnter={prefetchSettings}
                      onFocus={prefetchSettings}
                      className={cn(
                        "flex items-center justify-center p-2 rounded-xl transition-all duration-300 border",
                        pathname === "/settings"
                          ? "text-primary bg-primary/10 border-primary/20"
                          : "text-zinc-500 border-white/[0.05] hover:text-zinc-200 hover:bg-white/[0.05] hover:border-white/[0.12]",
                      )}
                      title="Settings"
                    >
                      <Settings className="w-4 h-4" />
                    </Link>
                  </div>
                  <div className="pl-1.5 border-l border-white/[0.1]">
                    <UserButton
                      afterSignOutUrl="/sign-in"
                      appearance={{
                        elements: {
                          userButtonAvatarBox:
                            "w-8 h-8 rounded-xl border border-white/20 shadow-xl",
                        },
                      }}
                    />
                  </div>
                </>
              ) : (
                <Button
                  size="sm"
                  asChild
                  className="rounded-xl font-bold bg-white text-black hover:bg-zinc-200"
                >
                  <Link href="/sign-in">Sign In</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <ExtensionPromoBanner />

      {/* Main Content */}
      <main className="max-w-7xl xl:max-w-[1300px] mx-auto px-4 md:px-8 lg:px-12 py-8">
        {children}
      </main>
    </div>
  );
}
